import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Editor } from '@/components/ui/editor';
import { toast } from '@/components/ui/sonner';
import { useCreateSource } from '@/hooks/use-sources';
import type { CreateSourceDto } from '@/types';
import { SaveArticleDialog, type ArticleSaveAs } from './dialogs/save-article-dialog';
import { FOR_PROTOCOL_NOTE } from '../constants';

interface CreateArticleViewProps {
  onCancel: () => void;
  onSaved: () => void;
}

// Full-page article editor (Figma "Create Article" screen) — replaces the Sources list
// content while active, rather than living inside a modal.
export function CreateArticleView({ onCancel, onSaved }: CreateArticleViewProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const createSourceMutation = useCreateSource();

  const handleSave = async (saveAs: ArticleSaveAs) => {
    if (!title.trim() || !content.trim()) {
      toast.error('Please enter both a title and content');
      return;
    }
    try {
      const sourceData: CreateSourceDto = {
        name: title.trim(),
        type: 'ARTICLE',
        value: content.trim(),
        metadata: { markAsSop: saveAs === 'sop' }
      };
      await createSourceMutation.mutateAsync(sourceData);
      toast.success('Article saved successfully', {
        ...(saveAs === 'sop' && { description: FOR_PROTOCOL_NOTE })
      });
      setSaveDialogOpen(false);
      onSaved();
    } catch (error) {
      toast.error('Failed to save article');
      console.error('Save article error:', error);
    }
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between gap-4'>
        <div className='flex items-center gap-3 flex-1 min-w-0'>
          <Button variant='ghost' size='icon' className='shrink-0 border-none shadow-none' onClick={onCancel}>
            <ChevronLeft className='size-5' />
          </Button>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder='Untitled Article'
            className='border-0 border-b border-dashed rounded-none shadow-none ring-0 focus-visible:ring-0 text-base font-semibold px-1 h-9'
          />
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          <Button variant='outline' className='text-xs' onClick={onCancel}>
            Cancel
          </Button>
          <Button className='text-xs' onClick={() => setSaveDialogOpen(true)} disabled={!title.trim() || !content.trim()}>
            Save As
          </Button>
        </div>
      </div>

      <Editor
        placeholder='Start typing...'
        value={content}
        contentType='markdown'
        onChange={setContent}
        showToolbar={false}
        className='border-0! ring-0 rounded-none min-h-[60vh] bg-transparent'
      />

      <SaveArticleDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        onSave={handleSave}
        isSaving={createSourceMutation.isPending}
      />
    </div>
  );
}
